import { cn } from './primitives'

/**
 * One coloured mark per Dispatch shipment type, drawn inline so the colour says the type before the word does
 * (verified list 2026-09-18: 1 Full Delivery, 2 Partial Delivery, 3 Reverse, 4 Return, 5 Exchange, 6 International).
 */
export type ShipmentTypeKey = 'delivery' | 'partial' | 'reverse' | 'return' | 'exchange' | 'international' | 'unknown'

const RULES: { key: ShipmentTypeKey; id?: number; re: RegExp }[] = [
  { key: 'partial', id: 2, re: /partial|جزئي/i },
  { key: 'reverse', id: 3, re: /reverse|عكسي/i },
  { key: 'return', id: 4, re: /return|مرتجع|رد/i },
  { key: 'exchange', id: 5, re: /exchange|تبديل|استبدال/i },
  { key: 'international', id: 6, re: /international|دولي/i },
  { key: 'delivery', id: 1, re: /full|delivery|توصيل|تسليم/i },
]
export function shipmentTypeKey(name?: string | null, id?: number | string | null): ShipmentTypeKey {
  const n = Number(id)
  if (n) return RULES.find((r) => r.id === n)?.key ?? 'unknown'
  const s = String(name ?? '').trim()
  if (!s) return 'unknown'
  return RULES.find((r) => r.re.test(s))?.key ?? 'unknown'
}

/** Brand-independent colours: the same green/amber/blue/red/violet/teal wherever the type appears. */
export const SHIPMENT_TYPE_TONE: Record<ShipmentTypeKey, { fg: string; bg: string }> = {
  delivery: { fg: '#16a34a', bg: '#e7f6ef' },
  partial: { fg: '#d97706', bg: '#fdf2e0' },
  reverse: { fg: '#2563eb', bg: '#e6eefc' },
  return: { fg: '#dc2626', bg: '#fdeaea' },
  exchange: { fg: '#7c3aed', bg: '#efe8fd' },
  international: { fg: '#0d9488', bg: '#e0f5f2' },
  unknown: { fg: '#64748b', bg: '#eef1f5' },
}

/** The mark alone (24-unit grid). */
export function ShipmentTypeIcon({ type, name, id, size = 16, className }: { type?: ShipmentTypeKey; name?: string | null; id?: number | string | null; size?: number; className?: string }) {
  const key = type ?? shipmentTypeKey(name, id)
  const c = SHIPMENT_TYPE_TONE[key]
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true, focusable: false as const, className: cn('keep-color shrink-0', className) }
  switch (key) {
    case 'delivery':
      // a sealed box with a tick: it goes out and gets handed over
      return (
        <svg {...common}>
          <path d="M12 2.8 20 6.8v10.4l-8 4-8-4V6.8l8-4Z" fill={c.fg} opacity=".18" />
          <path d="M12 2.8 20 6.8v10.4l-8 4-8-4V6.8l8-4Z" stroke={c.fg} strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M4.4 7 12 10.8 19.6 7M12 10.8V21" stroke={c.fg} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="17.2" cy="17" r="4.6" fill={c.fg} />
          <path d="m15.2 17 1.4 1.4 2.6-2.8" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'partial':
      // half the box filled: part of it is handed over
      return (
        <svg {...common}>
          <path d="M12 2.8 20 6.8v10.4l-8 4-8-4V6.8l8-4Z" stroke={c.fg} strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M4 6.8v10.4l8 4V10.8L4 6.8Z" fill={c.fg} opacity=".85" />
          <path d="M4.4 7 12 10.8 19.6 7M12 10.8V21" stroke={c.fg} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14.6 15.2h3.6" stroke={c.fg} strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      )
    case 'reverse':
      // the arrow points back to us: picked up from the customer
      return (
        <svg {...common}>
          <rect x="3" y="9" width="12" height="11" rx="2" fill={c.fg} opacity=".18" stroke={c.fg} strokeWidth="1.8" />
          <path d="M3.6 12.4h10.8" stroke={c.fg} strokeWidth="1.6" strokeLinecap="round" />
          <path d="M21 4.5H14.2a3 3 0 0 0-3 3V9" stroke={c.fg} strokeWidth="2" strokeLinecap="round" />
          <path d="m18.2 1.8 2.8 2.7-2.8 2.7" stroke={c.fg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" transform="rotate(180 19.6 4.5)" />
        </svg>
      )
    case 'return':
      // the U-turn: back to the sender
      return (
        <svg {...common}>
          <rect x="3" y="10" width="11" height="10" rx="2" fill={c.fg} opacity=".18" stroke={c.fg} strokeWidth="1.8" />
          <path d="M8 4.8h8.2a4.3 4.3 0 0 1 0 8.6H14" stroke={c.fg} strokeWidth="2" strokeLinecap="round" />
          <path d="m10.6 2.2-2.8 2.6 2.8 2.6" stroke={c.fg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3.6 13.6h9.8" stroke={c.fg} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      )
    case 'exchange':
      // two boxes swapping places
      return (
        <svg {...common}>
          <rect x="2.5" y="3" width="8.5" height="8" rx="1.8" fill={c.fg} opacity=".22" stroke={c.fg} strokeWidth="1.7" />
          <rect x="13" y="13" width="8.5" height="8" rx="1.8" fill={c.fg} stroke={c.fg} strokeWidth="1.7" />
          <path d="M14 7h5.5M19.5 7l-2.4-2.4M19.5 7l-2.4 2.4" stroke={c.fg} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10 17H4.5M4.5 17l2.4-2.4M4.5 17l2.4 2.4" stroke={c.fg} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'international':
      // a globe with a plane crossing it
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8.5" fill={c.fg} opacity=".16" stroke={c.fg} strokeWidth="1.8" />
          <path d="M3.5 12h17M12 3.5c3 3 3 14 0 17M12 3.5c-3 3-3 14 0 17" stroke={c.fg} strokeWidth="1.4" strokeLinecap="round" />
          <path d="m20.5 4.2-3.2 6.2-4.4-.9 1.2 1.2-2.2 1.1 3.1.8 1 3.1 1.1-2.3 1.3 1.1-.7-4.6L21.9 5Z" fill={c.fg} />
        </svg>
      )
    default:
      return (
        <svg {...common}>
          <path d="M12 2.8 20 6.8v10.4l-8 4-8-4V6.8l8-4Z" fill={c.fg} opacity=".15" stroke={c.fg} strokeWidth="1.8" strokeLinejoin="round" />
          <path d="M4.4 7 12 10.8 19.6 7M12 10.8V21" stroke={c.fg} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
  }
}

/** Mark + name, tinted: what a table cell or a fact line shows. */
export function ShipmentTypeChip({ name, id, size = 'sm', className, wrap = false }: { name?: string | null; id?: number | string | null; size?: 'sm' | 'md'; className?: string; /** the whole name on as many lines as it needs, never cut */ wrap?: boolean }) {
  const key = shipmentTypeKey(name, id)
  const c = SHIPMENT_TYPE_TONE[key]
  const label = String(name ?? '').trim() || '—'
  return (
    <span className={cn('inline-flex max-w-full items-center gap-1.5 rounded-lg font-semibold', size === 'md' ? 'px-2.5 py-1 text-[13px]' : 'px-2 py-0.5 text-xs', className)} style={{ background: c.bg, color: c.fg }} title={label}>
      <ShipmentTypeIcon type={key} size={size === 'md' ? 18 : 15} />
      <span className={wrap ? 'whitespace-normal break-words' : 'truncate'}>{label}</span>
    </span>
  )
}

/** What each type means, shown under its name in the picker (VSoft's wording, in both languages). */
export const SHIPMENT_TYPE_HELP: Record<ShipmentTypeKey, { en: string; ar: string }> = {
  delivery: { en: 'Deliver the entire shipment to the customer with all items included.', ar: 'توصيل الشحنة كاملة إلى العميل بجميع محتوياتها.' },
  partial: { en: 'Deliver only selected items; the remaining items stay pending or go back.', ar: 'تسليم جزء من الشحنة، ويبقى الباقي معلقا أو يعود إلى الراسل.' },
  reverse: { en: 'Retrieve a previously delivered shipment from the consignee and return it to the sender or warehouse.', ar: 'استرجاع شحنة سلمت سابقا من العميل وإعادتها إلى الراسل أو المخزن.' },
  return: { en: 'Pick up a shipment from the customer and return it to the sender or warehouse.', ar: 'استلام شحنة من العميل وإعادتها إلى الراسل أو المخزن.' },
  exchange: { en: 'Deliver a shipment and collect another one from the customer to return to the sender.', ar: 'توصيل شحنة إلى العميل واستلام شحنة أخرى منه لإعادتها إلى الراسل.' },
  international: { en: 'A shipment transported across borders from one country to another.', ar: 'شحنة تنقل بين دولتين.' },
  unknown: { en: '', ar: '' },
}

/**
 * The shipment type chooser used by Create / Edit Shipment: one card per type (coloured mark, name, what it means),
 * like VSoft's list but with the colour telling the type at a glance. Keyboard: arrow keys move, Enter/Space picks.
 */
export function ShipmentTypePicker({ types, value, onChange, lang, disabled }: { types: { id: number | string; name: string }[]; value: string; onChange: (id: string) => void; lang: 'ar' | 'en'; disabled?: boolean }) {
  return (
    <div role="radiogroup" className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
      {types.map((t) => {
        const key = shipmentTypeKey(t.name, t.id)
        const tone = SHIPMENT_TYPE_TONE[key]
        const on = String(t.id) === String(value)
        const help = SHIPMENT_TYPE_HELP[key][lang]
        return (
          <button
            key={String(t.id)}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(String(t.id))}
            className={cn(
              'relative flex items-start gap-3 rounded-xl border p-3 text-start transition-[border-color,box-shadow,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:opacity-60',
              on ? 'border-transparent shadow-sm' : 'border-border bg-surface hover:border-border-strong hover:bg-surface-hover',
            )}
            style={on ? { background: tone.bg, boxShadow: `0 0 0 2px ${tone.fg}` } : undefined}
          >
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl" style={{ background: on ? '#fff' : tone.bg }}>
              <ShipmentTypeIcon type={key} size={26} />
            </span>
            <span className="min-w-0 flex-1">
              <b className="block text-sm leading-snug" style={on ? { color: tone.fg } : undefined}>{t.name}</b>
              {help && <span className="mt-0.5 block text-xs leading-relaxed text-fg-muted">{help}</span>}
            </span>
            {on && (
              <span className="absolute end-2 top-2 flex size-5 items-center justify-center rounded-full text-white" style={{ background: tone.fg }} aria-hidden>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="m5 12.5 4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The coloured mark shaped like a Phosphor icon component, so every select / chip that takes an `icon` shows the
 * same coloured shipment-type mark as the create-shipment cards (one component per type, created once).
 */
const glyphs = new Map<ShipmentTypeKey, (p: { size?: number | string; className?: string }) => JSX.Element>()
export function shipmentTypeGlyph(key: ShipmentTypeKey) {
  let g = glyphs.get(key)
  if (!g) {
    g = ({ size = 16, className }) => <ShipmentTypeIcon type={key} size={Number(size) || 16} className={className} />
    glyphs.set(key, g)
  }
  return g
}
